import { Button } from "@freightsimple/ui-core/Controls/Button";
import { ControlPresentationProvider } from "@freightsimple/ui-core/Controls/ControlPresentationProvider";
import { Dialog } from "@freightsimple/ui-core/Dialog/Dialog";
import type { DialogBaseProps } from "@freightsimple/ui-core/Dialog/DialogBaseProps";
import type { UUID } from "@/Shared/types";
import type { Contact } from "@freightsimple/generated-dashboard-openapi-client/models/Contact";
import type { SavedContact } from "@freightsimple/generated-dashboard-openapi-client/models/SavedContact";
import { useState } from "react";
import { v4 as uuidv4 } from "uuid";
import ContactsLargeContactInput from "../Inputs/ContactsLargeContactInput";
import type { AddContactContactType } from "@/Shared/Types/contactTypes";
import {
  EmailRequired,
  contactsErrorMessagesForContact,
} from "../Validators/contactsErrorMessagesForContact";

interface ContactsAddContactDialogProps extends DialogBaseProps {
  savedLocationId: UUID;
  /** Fired with the new contact; the caller persists it. */
  onAddNewContact: (newContact: SavedContact) => void;
}

// "Add another contact" form in a dialog. Stacks on top of the manage-contacts
// dialog. Replaces the legacy imperative modal.
export function ContactsAddContactDialog(props: ContactsAddContactDialogProps) {
  const [newContact, setNewContact] = useState<AddContactContactType>({});
  const [forceValidation, setForceValidation] = useState(false);

  const errorMessages = contactsErrorMessagesForContact(
    newContact,
    EmailRequired.EmailNotRequired,
  );

  const isValid =
    !errorMessages.contactName &&
    !errorMessages.emailAddress &&
    !errorMessages.phoneNumber;

  function onAdd() {
    if (!isValid) {
      setForceValidation(true);
      return;
    }

    props.onAddNewContact({
      savedContactId: uuidv4(),
      savedLocationId: props.savedLocationId,
      contact: newContact as Contact,
    });
  }

  return (
    <Dialog
      open={props.open}
      onOpenChange={props.onOpenChange}
      title="Add another contact"
      width="narrow"
      footer={
        <>
          <Button onClick={() => props.onOpenChange?.(false)}>Cancel</Button>
          <Button primary onClick={onAdd}>
            Add New Contact
          </Button>
        </>
      }
    >
      <ControlPresentationProvider presentation="underlined">
        <ContactsLargeContactInput
          contact={newContact}
          contactChanged={function (updatedContact: AddContactContactType) {
            setNewContact({
              ...newContact,
              ...updatedContact,
            });
          }}
          forceValidation={forceValidation}
          errorMessages={errorMessages}
        />
      </ControlPresentationProvider>
    </Dialog>
  );
}
